import { Link } from 'react-router-dom';
import { ChevronRight, Plus } from 'lucide-react';
import { useChecklistRuns } from '@/lib/queries';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { runProgress, runStatusLabel } from '@/lib/checklist-helpers';
import type { ChecklistRun } from '@/lib/types';

/**
 * Checklists landing — operations console. Open runs at the top, a
 * single primary CTA to "Start a new run" (which routes to the
 * templates gallery), and recently-completed at the bottom. Templates
 * live one tap deeper at /checklists/templates so this page stays
 * focused on the in-progress state of the household.
 */
export default function Checklists() {
  const runs = useChecklistRuns({});

  const inProgress = (runs.data ?? []).filter(
    (r) => r.status === 'in_progress' || r.status === 'not_started',
  );
  const completed = (runs.data ?? []).filter((r) => r.status === 'completed').slice(0, 5);

  return (
    <div className="space-y-10">
      <header className="space-y-2">
        <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
          Operations · Checklists
        </p>
        <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">
          Checklists
        </h1>
        <p className="text-muted-foreground max-w-2xl">
          Templates capture the sequence; runs capture what you actually did. Tick items as
          you go. Notes per item survive across sessions.
        </p>
      </header>

      <section className="space-y-3" aria-labelledby="primary-cta">
        <h2 id="primary-cta" className="sr-only">
          Start a new run
        </h2>
        <Link
          to="/checklists/templates"
          className="block rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          data-testid="primary-cta-templates"
        >
          <Card className="border-primary/30 bg-primary/5 transition-colors hover:bg-primary/10">
            <CardContent className="flex items-center justify-between gap-3 p-5">
              <span className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10 text-primary">
                  <Plus className="h-5 w-5" aria-hidden />
                </span>
                <span className="space-y-0.5">
                  <span className="block font-display text-base">Start a new run</span>
                  <span className="block text-sm text-muted-foreground">
                    Pick a template or create one.
                  </span>
                </span>
              </span>
              <ChevronRight className="h-5 w-5 text-primary" aria-hidden />
            </CardContent>
          </Card>
        </Link>
      </section>

      <section className="space-y-3" aria-labelledby="open-runs">
        <div className="flex items-baseline justify-between border-b border-rule pb-2">
          <h2 id="open-runs" className="font-display text-xl font-semibold tracking-tight">
            Open runs
          </h2>
          {runs.data ? (
            <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground font-tabular">
              {inProgress.length} active
            </span>
          ) : null}
        </div>
        {runs.isLoading ? <p className="text-sm text-muted-foreground">Loading…</p> : null}
        {!runs.isLoading && inProgress.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No runs in progress. Start a new one above.
          </p>
        ) : null}
        {inProgress.length > 0 ? (
          <ul className="grid gap-3 md:grid-cols-2">
            {inProgress.map((r) => (
              <RunCard key={r.id} run={r} />
            ))}
          </ul>
        ) : null}
      </section>

      {completed.length > 0 ? (
        <section className="space-y-3" aria-labelledby="recent-completed">
          <div className="flex items-baseline justify-between border-b border-rule pb-2">
            <h2
              id="recent-completed"
              className="font-display text-xl font-semibold tracking-tight"
            >
              Recently completed
            </h2>
          </div>
          <ul className="grid gap-3 md:grid-cols-2">
            {completed.map((r) => (
              <RunCard key={r.id} run={r} />
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

function RunCard({ run }: { run: ChecklistRun }) {
  const pct = runProgress(run);
  return (
    <li>
      <Link
        to={`/checklists/runs/${run.id}`}
        className="group block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-lg"
        data-testid={`run-card-${run.id}`}
      >
        <Card className="transition-all border-rule group-hover:border-primary/30 group-hover:shadow-md">
          <CardHeader className="pb-2 space-y-1.5">
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              {runStatusLabel(run)}
            </p>
            <CardTitle className="font-display text-base leading-tight">{run.title}</CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-2">
            <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
              <div
                className="h-full bg-primary transition-[width] duration-200"
                style={{ width: pct === null ? '0%' : `${pct}%` }}
              />
            </div>
            <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground font-tabular">
              {pct === null ? '—' : `${pct}%`}
            </p>
          </CardContent>
        </Card>
      </Link>
    </li>
  );
}
